import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue } from 'framer-motion';
import { getAllProperties } from '../services/propertyService';
import PropertyCard from '../components/PropertyCard';
import Seo from '../components/seo/Seo';
import { SEOData } from '../components/seo/seoData';

export default function Home() {
  const [propsList, setPropsList] = useState([]);
  const trackRef = useRef(null);

  const y = useMotionValue(0);

  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragOffset = useRef(0);
  const idleTimeout = useRef(null);
  const isPaused = useRef(false);

  const frameRef = useRef(null);
  const trackHeight = useRef(0);

  const pageTitle = "Cuvia - Rezervari Maramures";

  const meta = SEOData.generateMeta({
    pageTitle,
    description:
      SEOData.siteDescription ||
      "Rezervati cele mai bune proprietati din Maramures pentru sejururi de neuitat.",
  });

  const og = SEOData.generateOG({
    pageTitle,
    description: meta.description,
    image: SEOData.ogImage || `${SEOData.siteUrl}/default-og.jpg`,
    url: SEOData.siteUrl,
  });

  const twitter = SEOData.generateTwitter({
    pageTitle,
    description: meta.description,
    image: og.image,
  });

  // FETCH
  useEffect(() => {
    const fetchProps = async () => {
      try {
        const data = await getAllProperties();
        setPropsList(data);
      } catch {
        setPropsList([]);
      }
    };

    fetchProps();
  }, []);

  // calculam inaltime track
  useEffect(() => {
    if (!trackRef.current) return;
    trackHeight.current = trackRef.current.scrollHeight / 2;
  }, [propsList]);

  // AUTOSCROLL
  useEffect(() => {
    const speed = 0.4;

    const tick = () => {
      if (!isDragging.current && !isPaused.current) {
        const current = y.get();

        // fallback sigur (în caz că nu e calculat încă)
        const height = trackRef.current?.scrollHeight / 2 || 1;

        // scroll continuu + wrap natural
        let next = current - speed;

        if (Math.abs(next) >= height) {
          next = next + height;
        }

        y.set(next);
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  // DRAG START
  const onDragStart = (e) => {
    isDragging.current = true;
    isPaused.current = true;

    e.preventDefault?.();

    dragStartY.current = e.touches?.[0]?.clientY || e.clientY;
    dragOffset.current = y.get();
  };

  // DRAG MOVE
  const onDragMove = (e) => {
    if (!isDragging.current) return;

    e.preventDefault?.();

    const pos = e.clientY || e.touches[0].clientY;
    const delta = pos - dragStartY.current;

    y.set(dragOffset.current + delta);
  };

  // DRAG END
  const onDragEnd = () => {
    isDragging.current = false;

    setTimeout(() => {
      isPaused.current = false;
    }, 300);
  };

  // LISTENERS
  useEffect(() => {
    const container = trackRef.current;
    if (!container) return;

    // mouse
    container.addEventListener('mousedown', onDragStart);
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);

    // touch (IMPORTANT: passive false)
    container.addEventListener('touchstart', onDragStart, { passive: true });
    container.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);

    return () => {
      container.removeEventListener('mousedown', onDragStart);
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);

      container.removeEventListener('touchstart', onDragStart);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
    };
  }, []);

  // duplicam lista pentru loop infinit
  const loopList = [...propsList, ...propsList];

  return (
    <div className="home-page">
      <Seo meta={meta} og={og} twitter={twitter} structuredData={[]} />

      {propsList.length === 0 ? (
        <p className="empty">Momentan nu sunt proprietati.</p>
      ) : (
        <div className="page">
          <motion.div
            ref={trackRef}
            className="motion-track"
            style={{ y }}
            onMouseEnter={() => (isPaused.current = true)}
            onMouseLeave={() => (isPaused.current = false)}
            onTouchStart={() => (isPaused.current = true)}
            onTouchEnd={() => (isPaused.current = false)}
          >
            {loopList.map((p, i) => (
              <PropertyCard key={i} p={p} className="property-card" />
            ))}
          </motion.div>
        </div>
      )}

      <footer>Bun venit pe Cuvia</footer>
    </div>
  );
}